import {
  ResolutionType,
  useScreenStore,
} from "application/contexts/screen/ScreenStore";
import { CategoryScrollRef } from "areas/_catalog/UseCatalog";
import React, {
  CSSProperties,
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useEffect,
  useRef,
} from "react";
import { debounce } from "throttle-debounce";
import { SeccionedProduct } from "../../../../domains/catalog/aggregates/SeccionedProduct";
import { ProductItem } from "../productItem/ProductItem";
import styles from "./Products.module.scss";

interface ProductsProps {
  seccionedProducts: SeccionedProduct[];
  onChangeCategory?: (index: number) => void;
  isClosed?: boolean;
  openDetail: (
    productId: string,
    refItem: React.RefObject<HTMLDivElement>,
    width?: string,
    height?: string
  ) => void;
  hideCategory: boolean;
}

const ProductsComponent = forwardRef<CategoryScrollRef, ProductsProps>(
  (
    { openDetail, seccionedProducts, isClosed, onChangeCategory, hideCategory },
    ref
  ) => {
    const { resolution } = useScreenStore();

    const refDiv = useRef<HTMLDivElement>(null);
    const isProgrammaticScroll = useRef(false);

    const smoothScrollTo = useCallback((element: HTMLElement, to: number) => {
      try {
        element.scrollTo({ top: to, behavior: "smooth" });
      } catch {
        element.scrollTop = to;
      }
    }, []);

    const categoryScroll = useCallback(
      (index: number) => {
        const _ref = refDiv?.current;
        if (_ref) {
          const section = _ref.children[index] as HTMLDivElement;
          if (section) {
            isProgrammaticScroll.current = true;

            // Evita "tremida" inicial ao agendar após o paint atual.
            requestAnimationFrame(() => {
              smoothScrollTo(_ref, section.offsetTop);
            });

            setTimeout(() => {
              isProgrammaticScroll.current = false;
            }, 600);
          }
        }
      },
      [smoothScrollTo]
    );

    useImperativeHandle(ref, () => ({
      categoryScroll: (index: number) => {
        categoryScroll(index);
      },
    }));

    useEffect(() => {
      const handleScroll = debounce(200, () => {
        if (isProgrammaticScroll.current) return;

        const element = refDiv.current;
        if (!element) return;

        const scrollPosition = element.scrollTop;
        let activeIndex = -1;

        Array.from(element.children).forEach((child, index) => {
          const { offsetTop, offsetHeight } = child as HTMLDivElement;
          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            activeIndex = index;
          }
        });

        if (onChangeCategory && activeIndex !== -1) {
          onChangeCategory(activeIndex);
        }
      });

      const element = refDiv.current;
      element?.addEventListener("scroll", handleScroll);
      return () => {
        element?.removeEventListener("scroll", handleScroll);
      };
    }, [refDiv, onChangeCategory]);

    return (
      <div
        id={styles.Products}
        style={
          {
            "--margin-size":
              resolution === ResolutionType.FULL_HD
                ? hideCategory
                  ? "12px"
                  : "22px"
                : hideCategory
                ? "6px"
                : "12px",
          } as CSSProperties
        }
      >
        <div
          ref={refDiv}
          id="product-scroll-container"
          className={styles.overflow}
          style={{
            width: hideCategory
              ? `calc(100% - ${
                  resolution === ResolutionType.FULL_HD ? "85" : "70"
                }px)`
              : `calc(100% - ${
                  resolution === ResolutionType.FULL_HD ? "250" : "200"
                }px)`,
          }}
        >
          {seccionedProducts.map((section, index) => {
            if (section.products.length === 0) return null;

            return (
              <div key={section.id ?? index}>
                <div
                  style={{
                    fontSize: resolution === ResolutionType.FULL_HD ? 30 : 22,
                  }}
                >
                  {section.title}
                </div>
                {section.products.map((produto) => (
                  <ProductItem
                    key={produto.id}
                    hideCategory={hideCategory}
                    hidePrice={false}
                    product={produto}
                    isClosed={isClosed}
                    openDetail={openDetail}
                  />
                ))}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
);

ProductsComponent.displayName = "Products";

export const Products = memo(ProductsComponent);
